// Market Watch: the overview board, stocks on the left and macro (rates, volatility, dollar, crude, gold, Bitcoin) on
// the right, under a market-state header. A US index row is always its index; outside the regular session the stocks
// side gains a third column (decided Oct 26, Design Desk option A): the move since the close before the open ("Pre"),
// after it ("After") and overnight ("Fut"), and the week when everything is shut ("Week"). Before the open the day
// column is yesterday's, so it takes yesterday's name and dims. Prices only, from the sidecar (Yahoo, delayed;
// Bitcoin from CoinGecko).
import { registerDisplay } from '../navigation.mjs';
import { escapeHtml } from '../utils/string.mjs';
import SnapshotDisplay from './snapshot-display.mjs';
import { data } from './snapshot.mjs';
import { asOf, price, percent } from './format.mjs';

const { markets } = window.RHL;

const nyParts = (now) => {
	const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
		timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: 'numeric', minute: 'numeric', hourCycle: 'h23', weekday: 'short',
	}).formatToParts(now).map((p) => [p.type, p.value]));
	return {
		day: `${parts.year}-${parts.month}-${parts.day}`,
		weekday: parts.weekday,
		minutes: Number(parts.hour) * 60 + Number(parts.minute),
	};
};

// the NYSE session and the CME equity futures session, by the clock in New York
const marketState = (now = new Date()) => {
	const { day, weekday, minutes } = nyParts(now);
	const weekend = weekday === 'Sat' || weekday === 'Sun';
	const holiday = markets.holidays.includes(day);
	const close = markets.earlyCloses.includes(day) ? 13 * 60 : 16 * 60;
	let stocks = 'Markets closed';
	if (!weekend && !holiday) {
		if (minutes >= 9 * 60 + 30 && minutes < close) stocks = 'Markets open';
		else if (minutes >= 4 * 60 && minutes < 9 * 60 + 30) stocks = 'Pre-market';
		else if (minutes >= close && minutes < close + 4 * 60) stocks = 'After hours';
	}
	const futuresClosed = weekday === 'Sat'
		|| (weekday === 'Sun' && minutes < 18 * 60)
		|| (weekday === 'Fri' && minutes >= 17 * 60)
		|| (minutes >= 17 * 60 && minutes < 18 * 60);
	return { stocks, futures: futuresClosed ? 'Futures closed' : 'Futures open', futuresOpen: !futuresClosed };
};

// colored by the change as shown, so a change that rounds to 0.0% stays white
// every market percentage shows two places (Oct 26, Jared: +0.8 beside +0.82 read unevenly)
const PLACES = 2;
const direction = (value) => {
	const rounded = Math.round(value * 100) / 100;
	if (rounded > 0) return 'up';
	if (rounded < 0) return 'dn';
	return '';
};

// a yield shows as a percent with its change in basis points, from the quote's own percent change
const yieldCells = (quote) => {
	const before = quote.last / (1 + quote.changePct / 100);
	const bp = Math.round((quote.last - before) * 100);
	// colored the other way round: a rising yield is red (decided Oct 26)
	return `<div class="r">${quote.last.toFixed(2)}%</div>
		<div class="r ${direction(-bp)}">${bp > 0 ? '+' : ''}${bp}bp</div>`;
};
const labelFor = (instrument, quote) => quote?.label ?? instrument.label;

// the third column on the stocks side, by the clock: null while the NYSE is open
const extraColumn = (state) => {
	if (state.stocks === 'Markets open') return null;
	if (state.stocks === 'Pre-market') return { key: 'since', head: 'Pre', note: 'Pre: each ETF since the close' };
	if (state.stocks === 'After hours') return { key: 'since', head: 'After', note: 'After: each ETF since the close' };
	if (state.futuresOpen) return { key: 'since', head: 'Fut', note: 'Fut: each future since the close' };
	return { key: 'week', head: 'Week', note: 'Week: change over five sessions' };
};

const laDay = (date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(date);
const nyWeekday = (date) => new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', weekday: 'short' }).format(date);
// a day change that belongs to an earlier day (before the open, on a weekend) dims, and the column takes its name
const isOld = (quote, now) => !!quote?.at && laDay(new Date(quote.at)) !== laDay(now);
const dayHeader = (quotes, now) => {
	const us = markets.board.filter((i) => i.etf).map((i) => quotes[i.key]).find((q) => q?.at);
	return us && isOld(us, now) ? nyWeekday(new Date(us.at)) : 'Day';
};

const weekChange = (key, quote) => {
	const closes = data('history')?.[key]?.closes;
	const from = closes?.length >= 6 ? closes.at(-6).c : null;
	return from && quote?.last ? (quote.last / from - 1) * 100 : null;
};
const extraCell = (instrument, quote, extra) => {
	if (!extra) return '';
	const value = extra.key === 'week' ? weekChange(instrument.key, quote) : quote?.since?.pct;
	if (value === null || value === undefined) return '<div class="r"></div>';
	return `<div class="r ${extra.key === 'week' ? direction(value) : 'since'}">${percent(value, PLACES)}</div>`;
};

const cells = (instrument, quotes, now, extra) => {
	const quote = quotes[instrument.key];
	if (!quote) return `<div class="name">${escapeHtml(instrument.label)}</div><div class="r">--</div><div class="r"></div>`;
	// only the US rows: Asia's session ends in our evening, so its "today" is often yesterday here
	const old = instrument.etf && isOld(quote, now);
	return `<div class="name${quote.stale ? ' stale' : ''}">${escapeHtml(labelFor(instrument, quote))}</div>
		${instrument.unit === 'yield' ? yieldCells(quote) : `<div class="r">${price(quote.last)}</div>
		<div class="r ${old ? 'old' : direction(quote.changePct)}">${percent(quote.changePct, PLACES)}</div>`}`;
};

// the board itself; Market Close-Up opens with it too
const boardHtml = (now) => {
	const quotes = data('quotes');
	const state = marketState(now);
	const extra = extraColumn(state);
	const blanks = (n) => '<div></div>'.repeat(n);
	const right = markets.macro ?? [];
	const rows = Array.from({ length: Math.max(markets.board.length, right.length) }, (_, i) => {
		const left = markets.board[i];
		const macro = right[i];
		const leftCells = left ? cells(left, quotes, now) + extraCell(left, quotes[left.key], extra) : blanks(extra ? 4 : 3);
		return `${leftCells}<div></div>${macro ? cells(macro, quotes, now) : blanks(3)}`;
	});
	const newest = Object.values(quotes).map((q) => q.at).sort().at(-1);
	return `<div class="hdr state">${state.stocks} &middot; ${state.futures}</div>
		<div class="board${extra ? ' extra' : ''}">
			<div class="th">Stocks</div><div class="th r">Last</div><div class="th r">${dayHeader(quotes, now)}</div>${extra ? `<div class="th r">${extra.head}</div>` : ''}<div></div>
			<div class="th">Macro</div><div class="th r">Last</div><div class="th r">Chg</div>
			${rows.join('')}
		</div>
		<div class="asof">${extra ? `${extra.note} &middot; ` : 'Delayed prices &middot; '}${asOf(newest, now)}</div>`;
};

class MarketWatch extends SnapshotDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, 'Market Watch', { seconds: 18 });
	}

	// eslint-disable-next-line class-methods-use-this
	hasData() {
		return Object.keys(data('quotes') ?? {}).length > 0;
	}

	// eslint-disable-next-line class-methods-use-this
	render(now) {
		return boardHtml(now);
	}
}

registerDisplay(new MarketWatch(16, 'market-watch'));

export default marketState;
export {
	boardHtml, direction, labelFor, extraColumn, PLACES,
};
